import React from 'react';
import { TaskInventory as TaskInventoryType, CustomUnitsDefinition } from '@bringg/types';
import CardWrapper from '../../card/card-wrapper';
import { get as _get, isNil as _isNil, capitalize as _capitalize } from 'lodash';
import { TASK_INVENTORY_CONSTANTS } from './task-inventory-form';
import Label from '../../label/label';
import { Controller, UseFormMethods } from 'react-hook-form';
import NumberInput from '../../number-input/number-input';
import Input from '../../floating-placeholder-input/input';
import { Translations } from '../task-inventory';
import { TaskInventoriesFlagsAccess } from 'features/waypoint-card/waypoint-card.consts';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { Button } from 'components';

interface Props {
	taskInventory: TaskInventoryType;
	depth: number;
	translations: Translations;
	form: UseFormMethods;
	onDelete: (taskInventoryID: number) => void;
	addToTaskInventoryMap: (taskInventoryID: number, taskInventory: TaskInventoryType) => void;
	flagsAccess: TaskInventoriesFlagsAccess;
	customUnitsDefinition: CustomUnitsDefinition;
}

const TaskInventoryFormItems: React.FC<Props> = ({
	taskInventory,
	depth,
	translations,
	form,
	onDelete,
	addToTaskInventoryMap,
	flagsAccess,
	customUnitsDefinition
}: Props) => {
	const getHandlingUnitsFields = taskInventory => {
		const handlingUnitsFields = [];
		for (const key in customUnitsDefinition) {
			handlingUnitsFields.push(
				<div
					className="task-inventory-handling-units task-inventory-card-inputs-container"
					key={`${taskInventory.id}-${key}`}
				>
					<BringgIcon iconName={BringgFontIcons.Pallets} className="inventory-icon" />
					<div className={`task-inventory-card-${key}`}>
						{getController(
							_capitalize(customUnitsDefinition[key].name),
							form,
							`${taskInventory.id}-${TASK_INVENTORY_CONSTANTS.handling_units}-${key}`,
							_isNil(taskInventory.handling_units?.[key])
								? taskInventory.inventory?.handling_units?.[key]
								: taskInventory.handling_units?.[key]
						)}
					</div>
				</div>
			);
		}
		return handlingUnitsFields;
	};

	const validateNumberInput = value => {
		if (value.currentTarget.value < 0) {
			value.currentTarget.value = 0;
		}
	};

	const taskInventoryName = `${taskInventory.original_quantity} x ${_get(
		taskInventory,
		'name',
		taskInventory.inventory_id
	)}`;
	const taskInventoryExternalId = _get(taskInventory, 'external_id');
	const scanString = _get(taskInventory, 'scan_string') || 'N.A';

	const getCardBody = taskInventory => (
		<div className="task-inventory-card-body">
			<div className="task-inventory-card-inputs-container">
				<BringgIcon iconName={BringgFontIcons.Ruler} className="inventory-icon" />
				<div className="task-inventory-card-length">
					{getController(
						translations.inventoryLength,
						form,
						`${taskInventory.id}-` + TASK_INVENTORY_CONSTANTS.length,
						_isNil(taskInventory.length) ? taskInventory.inventory?.length : taskInventory.length
					)}
				</div>
				<div className="task-inventory-card-width">
					{getController(
						translations.width,
						form,
						`${taskInventory.id}-` + TASK_INVENTORY_CONSTANTS.width,
						_isNil(taskInventory.width) ? taskInventory.inventory?.width : taskInventory.width
					)}
				</div>
				<div className="task-inventory-card-height">
					{getController(
						translations.height,
						form,
						`${taskInventory.id}-` + TASK_INVENTORY_CONSTANTS.height,
						_isNil(taskInventory.height) ? taskInventory.inventory?.height : taskInventory.height
					)}
				</div>
			</div>
			<div className="task-inventory-weight task-inventory-card-inputs-container">
				<BringgIcon iconName={BringgFontIcons.Weight} className="inventory-icon" />
				<div className="task-inventory-card-weight">
					{getController(
						translations.weight,
						form,
						`${taskInventory.id}-` + TASK_INVENTORY_CONSTANTS.weight,
						_isNil(taskInventory.weight) ? taskInventory.inventory?.weight : taskInventory.weight
					)}
				</div>
			</div>
			{getHandlingUnitsFields(taskInventory)}
			<div className="task-inventory-card-details">
				<div className="task-inventory-card-details-item">
					<BringgIcon iconName={BringgFontIcons.Coins} className="inventory-icon" />
					<span className="task-inventory-card-details-item-span">
						{_get(taskInventory, 'price') || 'N.A'}
					</span>
				</div>
				<div className="task-inventory-card-details-item" title={scanString}>
					<BringgIcon iconName={BringgFontIcons.Barcode} className="inventory-icon" />
					<span className="text-ellipsis">{scanString}</span>
				</div>
			</div>
		</div>
	);
	const getCardHeader = taskInventory => (
		<div className="task-inventory-card-header">
			<div className="task-inventory-card-content">
				<span>
					{taskInventory.pending ? (
						<Label text={`${translations.collectText}`} size="small" className="orange" />
					) : (
						<Label text={`${translations.deliverText}`} size="small" className="green" />
					)}
				</span>
				<span className="task-inventory-card-name text-ellipsis" title={taskInventoryName}>
					{taskInventoryName}
				</span>
				<span className="task-inventory-card-id text-ellipsis" title={taskInventoryExternalId}>
					#{taskInventoryExternalId}
				</span>
			</div>
			<div>
				<div className="task-inventory-edit-quantity">
					<Controller
						render={({ value, onChange }) => (
							<NumberInput
								title={translations.quantity}
								minValue={0}
								value={value}
								onChange={onChange}
								isDisabled={!flagsAccess.enable_dispatcher_edit_task_inventory_quantity}
							/>
						)}
						control={form.control}
						defaultValue={taskInventory.original_quantity}
						name={`${taskInventory.id}-` + TASK_INVENTORY_CONSTANTS.originalQuantity}
					/>
				</div>
			</div>
		</div>
	);
	const getController = (placeholder: string, form: UseFormMethods, name: string, defaultValue: number) => (
		<>
			<Controller
				render={({ onChange, value }) => (
					<Input
						type={'number'}
						placeholder={placeholder}
						className="full-border-container"
						inputClassName="full-border"
						isDisabled={!flagsAccess.enable_dimension_editing_in_ui}
						onChange={value => {
							validateNumberInput(value);
							onChange(value);
						}}
						defaultValue={value}
					/>
				)}
				control={form.control}
				name={name}
				defaultValue={defaultValue || 0}
			/>
		</>
	);
	return (
		<>
			{addToTaskInventoryMap(taskInventory.id, taskInventory)}
			<CardWrapper
				style={{ marginLeft: `${depth * 3}%` }}
				title={getCardHeader(taskInventory)}
				className={`task-inventory-${taskInventory.id.toString()}`}
				extra={
					<Button
						className="trash-button"
						onClick={() => onDelete(taskInventory.id)}
						danger
						icon={<BringgIcon iconName={BringgFontIcons.Trash} className="trash-icon" />}
						type="link"
					/>
				}
			>
				{getCardBody(taskInventory)}
			</CardWrapper>
			{taskInventory.inventories?.length > 0 &&
				taskInventory.inventories.map(subTaskInventory => (
					<TaskInventoryFormItems
						key={subTaskInventory.id.toString()}
						translations={translations}
						onDelete={onDelete}
						taskInventory={subTaskInventory}
						form={form}
						depth={depth + 1}
						addToTaskInventoryMap={addToTaskInventoryMap}
						flagsAccess={flagsAccess}
						customUnitsDefinition={customUnitsDefinition}
					/>
				))}
		</>
	);
};

export default TaskInventoryFormItems;
